import { skipToken } from "@reduxjs/toolkit/query";
import {
  getOutcomeDefinitionFromPlaybook,
  type AdminApiPlaybook,
} from "@repo/app-types/entities/playbook";
import type {
  PlaybookTestAssertion,
  CallTestAssertionKind,
  PlaybookTestToolCallAssertion,
} from "@repo/app-types/entities/playbookTestCase";
import {
  allCallTestAssertionKinds,
  playbookTestAssertionKind,
  playbookTestContentScope,
} from "@repo/app-types/entities/playbookTestCase";
import type { Tool } from "@repo/app-types/entities/tool";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { FieldDescription } from "@repo/ui/field/fieldDescription";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { SimpleMenu } from "@repo/ui/Menu/simpleMenu";
import { SimpleMenuItem } from "@repo/ui/Menu/simpleMenuItem";
import { SimpleMenuPopover } from "@repo/ui/Menu/simpleMenuPopover";
import {
  SimpleSelect,
  type SimpleSelectOption,
} from "@repo/ui/Select/simpleSelect";
import { Switch } from "@repo/ui/Switch/switch";
import { SegmentedControl } from "@repo/ui/toggleButton/segmentedControl";
import {
  IconChecklist,
  IconChevronDown,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { useMemo, type FC } from "react";
import {
  Button as AriaButton,
  Disclosure,
  DisclosurePanel,
  Heading,
  MenuTrigger,
} from "react-aria-components";
import { useParams } from "react-router";
import { v4 as uuidv4 } from "uuid";
import { useGetTeammateAgentQuery } from "../../api/entities/teammateAgent.js";
import { useAllToolsById } from "../../hooks/useAllToolsById.js";

type PlaybookTestToolCallArgument = NonNullable<
  PlaybookTestToolCallAssertion["arguments"]
>[number];

const assertionKindLabels: Record<CallTestAssertionKind, string> = {
  [playbookTestAssertionKind.Outcome]: "Outcome",
  [playbookTestAssertionKind.ToolCall]: "Tool call",
  [playbookTestAssertionKind.FactCaptured]: "Fact captured",
  [playbookTestAssertionKind.Content]: "What it said",
};

const toolCallExpectationOptions = [
  { id: "called", label: "Called" },
  { id: "not_called", label: "Not called" },
] as const;

const contentExpectationOptions = [
  { id: "present", label: "Present" },
  { id: "absent", label: "Absent" },
] as const;

const contentScopeOptions = [
  { id: playbookTestContentScope.Speech, label: "What the agent said" },
  { id: playbookTestContentScope.Call, label: "How the call went" },
] as const;

const argumentConstraintKind = {
  Equals: "equals",
  Matches: "matches",
} as const;
type ArgumentConstraintKind =
  (typeof argumentConstraintKind)[keyof typeof argumentConstraintKind];

const argumentConstraintOptions: readonly SimpleSelectOption<ArgumentConstraintKind>[] =
  [
    { value: argumentConstraintKind.Equals, label: "Equals" },
    { value: argumentConstraintKind.Matches, label: "Matches" },
  ];

/**
 * A new row of the kind that was picked. The id is generated here and never
 * again: a verdict from an earlier run is stored against it, so renumbering on
 * edit would hand a row somebody else's result.
 */
const createPlaybookTestAssertion = (
  kind: CallTestAssertionKind,
): PlaybookTestAssertion => {
  const id = uuidv4();

  switch (kind) {
    case playbookTestAssertionKind.Outcome:
      return { id, kind, outcome: "" };
    case playbookTestAssertionKind.ToolCall:
      return { id, kind, tool_id: "", expected: "called" };
    case playbookTestAssertionKind.FactCaptured:
      return { id, kind, fact: "" };
    case playbookTestAssertionKind.Content:
      return { id, kind, statement: "", expected: "present" };
  }
};

/** A row still waiting for the one field its kind is about. */
const checkAssertionIncomplete = (
  assertion: PlaybookTestAssertion,
): boolean => {
  switch (assertion.kind) {
    case playbookTestAssertionKind.Outcome:
      return assertion.outcome.trim() === "";
    case playbookTestAssertionKind.ToolCall:
      return assertion.tool_id === "";
    case playbookTestAssertionKind.FactCaptured:
      return assertion.fact.trim() === "";
    case playbookTestAssertionKind.Content:
      return assertion.statement.trim() === "";
  }
};

/** A constraint with no path, or with neither an exact value nor a pattern. */
const checkArgumentIncomplete = (
  argument: PlaybookTestToolCallArgument,
): boolean =>
  argument.path.trim() === "" ||
  ((argument.equals ?? "") === "" && (argument.matches ?? "") === "");

/**
 * The assertions as they leave the editor: a row nobody finished is nothing
 * yet, not a claim the case is graded on. The editor keeps its own empty rows
 * so they stay editable.
 */
export const dropIncompletePlaybookTestAssertions = (
  assertions: readonly PlaybookTestAssertion[] | null,
): PlaybookTestAssertion[] | null => {
  if (!assertions) {
    return null;
  }

  return assertions
    .filter((assertion) => !checkAssertionIncomplete(assertion))
    .map((assertion) => {
      if (assertion.kind !== playbookTestAssertionKind.ToolCall) {
        return assertion;
      }

      const argumentConstraints = (assertion.arguments ?? []).filter(
        (argument) => !checkArgumentIncomplete(argument),
      );

      return {
        ...assertion,
        // Left off rather than stored empty, so a plain "it called the tool"
        // assertion carries nothing it does not constrain.
        arguments:
          argumentConstraints.length === 0 ? undefined : argumentConstraints,
      };
    });
};

/**
 * What the case claims, in one line, so a collapsed section says how much it is
 * graded on: "5 assertions, 2 diagnostic".
 */
export const describePlaybookTestAssertions = (
  assertions: readonly PlaybookTestAssertion[] | null,
): string => {
  const complete = (assertions ?? []).filter(
    (assertion) => !checkAssertionIncomplete(assertion),
  );

  if (complete.length === 0) {
    return "Not set";
  }

  const diagnosticCount = complete.filter(
    (assertion) => assertion.diagnostic === true,
  ).length;

  const assertionCount =
    complete.length === 1 ? "1 assertion" : `${complete.length} assertions`;

  return diagnosticCount === 0
    ? assertionCount
    : `${assertionCount}, ${diagnosticCount} diagnostic`;
};

/**
 * The tools the agent under test is configured with, in the agent's own order.
 * An assertion can be made about any of them, custom or not. Both queries are
 * already loaded by the surfaces around this one, so the editor reads them from
 * the cache rather than fetching anything of its own.
 */
const useAttachedAgentTools = (): readonly Tool[] => {
  const { teammateAgentId } = useParams();

  const { data: teammateAgent } = useGetTeammateAgentQuery(
    teammateAgentId ?? skipToken,
  );
  const toolsById = useAllToolsById();

  return useMemo(
    () =>
      (teammateAgent?.teammate_agent_config.tool_ids ?? [])
        .map((toolId) => toolsById.get(toolId))
        .filter((tool): tool is Tool => tool !== undefined),
    [teammateAgent, toolsById],
  );
};

interface ToolCallArgumentListProps {
  value: readonly PlaybookTestToolCallArgument[];
  onChange: (value: PlaybookTestToolCallArgument[]) => void;
}

/**
 * The constraints on what the tool was called with, behind their own
 * disclosure so "it called the booking tool" stays one line.
 */
const ToolCallArgumentList: FC<ToolCallArgumentListProps> = (props) => {
  const { value, onChange } = props;

  const updateArgument = (
    index: number,
    argument: PlaybookTestToolCallArgument,
  ) => {
    onChange(
      value.map((existing, existingIndex) =>
        existingIndex === index ? argument : existing,
      ),
    );
  };

  return (
    <Disclosure>
      <Heading>
        <AriaButton
          slot="trigger"
          className="group flex cursor-pointer items-center gap-1 text-sm text-muted-foreground"
        >
          <IconChevronDown className="size-4 shrink-0 transition-transform group-aria-expanded:rotate-180" />
          {value.length === 0
            ? "Arguments"
            : `Arguments (${value.length.toString()})`}
        </AriaButton>
      </Heading>
      <DisclosurePanel>
        <div className="flex flex-col gap-2 pt-2">
          <FieldDescription>
            Each constraint is a path into the arguments the agent sent, and
            either the exact value it must equal or a pattern it must match.
          </FieldDescription>
          {value.map((argument, index) => {
            const constraintKind: ArgumentConstraintKind =
              argument.matches === undefined
                ? argumentConstraintKind.Equals
                : argumentConstraintKind.Matches;
            const constraintValue =
              constraintKind === argumentConstraintKind.Equals
                ? (argument.equals ?? "")
                : (argument.matches ?? "");

            return (
              <div key={index} className="flex items-start gap-2">
                <div className="flex-1">
                  <Input
                    accessibleLabel="Argument path"
                    placeholder="appointment.phone_number"
                    value={argument.path}
                    onChange={(event) =>
                      updateArgument(index, {
                        ...argument,
                        path: event.target.value,
                      })
                    }
                  />
                </div>
                <div className="w-32 shrink-0">
                  <SimpleSelect<ArgumentConstraintKind>
                    label="Constraint"
                    isLabelHidden
                    isFullWidth
                    options={argumentConstraintOptions}
                    value={constraintKind}
                    onChange={(newConstraintKind) =>
                      updateArgument(
                        index,
                        newConstraintKind === argumentConstraintKind.Equals
                          ? { path: argument.path, equals: constraintValue }
                          : { path: argument.path, matches: constraintValue },
                      )
                    }
                  />
                </div>
                <div className="flex-1">
                  <Input
                    accessibleLabel="Argument value"
                    placeholder={
                      constraintKind === argumentConstraintKind.Equals
                        ? "+15551234567"
                        : "^\\+1\\d{10}$"
                    }
                    value={constraintValue}
                    onChange={(event) =>
                      updateArgument(
                        index,
                        constraintKind === argumentConstraintKind.Equals
                          ? {
                              path: argument.path,
                              equals: event.target.value,
                            }
                          : {
                              path: argument.path,
                              matches: event.target.value,
                            },
                      )
                    }
                  />
                </div>
                <IconButton
                  icon={<IconTrash className="h-4 w-4" />}
                  variant={ButtonVariant.Tertiary}
                  accessibleLabel="Remove argument constraint"
                  onClick={() =>
                    onChange(
                      value.filter(
                        (_, existingIndex) => existingIndex !== index,
                      ),
                    )
                  }
                />
              </div>
            );
          })}
          <div>
            <Button
              variant={ButtonVariant.Secondary}
              size={Size.MEDIUM}
              icon={<IconPlus className="h-4 w-4" />}
              onClick={() => onChange([...value, { path: "", equals: "" }])}
            >
              Add argument constraint
            </Button>
          </div>
        </div>
      </DisclosurePanel>
    </Disclosure>
  );
};

interface PlaybookTestAssertionRowProps {
  assertion: PlaybookTestAssertion;
  outcomeOptions: readonly SimpleSelectOption<string>[];
  tools: readonly Tool[];
  onChange: (assertion: PlaybookTestAssertion) => void;
  onRemove: () => void;
}

/** One claim about the call: its kind's fields, and whether it gates. */
const PlaybookTestAssertionRow: FC<PlaybookTestAssertionRowProps> = (props) => {
  const { assertion, outcomeOptions, tools, onChange, onRemove } = props;

  const toolOptions: readonly SimpleSelectOption<string>[] = tools.map(
    (tool) => ({ value: tool.id, label: tool.name }),
  );

  return (
    <div className="flex flex-col gap-2 rounded-md border border-border-light p-3">
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1 text-sm font-medium text-foreground">
          {assertionKindLabels[assertion.kind]}
        </div>
        <IconButton
          icon={<IconTrash className="h-4 w-4" />}
          variant={ButtonVariant.Tertiary}
          accessibleLabel="Remove assertion"
          onClick={onRemove}
        />
      </div>
      {assertion.kind === playbookTestAssertionKind.Outcome ? (
        outcomeOptions.length === 0 ? (
          <FieldDescription>
            This playbook has no outcome to assert on.
          </FieldDescription>
        ) : (
          <SimpleSelect<string>
            label="Expected outcome"
            isFullWidth
            placeholder="Pick an outcome"
            options={outcomeOptions}
            value={assertion.outcome === "" ? null : assertion.outcome}
            onChange={(outcome) => onChange({ ...assertion, outcome })}
          />
        )
      ) : null}
      {assertion.kind === playbookTestAssertionKind.ToolCall ? (
        <>
          {tools.length === 0 ? (
            <FieldDescription>
              This agent has no tools to assert on.
            </FieldDescription>
          ) : (
            <SimpleSelect<string>
              label="Tool"
              isFullWidth
              placeholder="Pick a tool"
              options={toolOptions}
              value={assertion.tool_id === "" ? null : assertion.tool_id}
              onChange={(toolId) => onChange({ ...assertion, tool_id: toolId })}
            />
          )}
          <SegmentedControl
            aria-label="Tool call expectation"
            size={Size.SMALL}
            options={toolCallExpectationOptions}
            value={assertion.expected}
            onChange={(expected) => onChange({ ...assertion, expected })}
          />
          <ToolCallArgumentList
            value={assertion.arguments ?? []}
            onChange={(argumentConstraints) =>
              onChange({
                ...assertion,
                arguments:
                  argumentConstraints.length === 0
                    ? undefined
                    : argumentConstraints,
              })
            }
          />
        </>
      ) : null}
      {assertion.kind === playbookTestAssertionKind.FactCaptured ? (
        <Input
          label="Fact"
          value={assertion.fact}
          onChange={(event) =>
            onChange({ ...assertion, fact: event.target.value })
          }
          placeholder="The customer's move-in date"
        />
      ) : null}
      {assertion.kind === playbookTestAssertionKind.Content ? (
        <>
          <Input
            label="Statement"
            value={assertion.statement}
            onChange={(event) =>
              onChange({ ...assertion, statement: event.target.value })
            }
            placeholder="The agent quoted the monthly rate."
          />
          <SegmentedControl
            aria-label="Statement expectation"
            size={Size.SMALL}
            options={contentExpectationOptions}
            value={assertion.expected}
            onChange={(expected) => onChange({ ...assertion, expected })}
          />
          <SegmentedControl
            aria-label="Statement scope"
            size={Size.SMALL}
            options={contentScopeOptions}
            value={assertion.scope ?? playbookTestContentScope.Speech}
            onChange={(scope) =>
              onChange({
                ...assertion,
                // Left off for speech, which is what an assertion without one
                // has always meant.
                scope:
                  scope === playbookTestContentScope.Call ? scope : undefined,
              })
            }
          />
          <FieldDescription>
            {assertion.scope === playbookTestContentScope.Call
              ? "The statement holds when the agent said or did it, or the call went that way."
              : "The statement holds only when the agent said it."}{" "}
            &ldquo;Absent&rdquo; is how a guardrail is written: the call fails
            if it holds.
          </FieldDescription>
        </>
      ) : null}
      <Switch
        label="Diagnostic"
        isSelected={assertion.diagnostic ?? false}
        onChange={(diagnostic) =>
          onChange({
            ...assertion,
            // Left off rather than stored as false, so an untouched toggle
            // reads as the default it is.
            diagnostic: diagnostic ? true : undefined,
          })
        }
      />
      <FieldDescription>
        A diagnostic assertion is judged and shown, but never fails the case.
      </FieldDescription>
    </div>
  );
};

export interface PlaybookTestAssertionsEditorProps {
  playbook: AdminApiPlaybook | undefined;
  tools: readonly Tool[];
  value: readonly PlaybookTestAssertion[];
  onChange: (value: PlaybookTestAssertion[]) => void;
}

/**
 * The claims this case is graded on. Each is judged on its own, and every one
 * that is not diagnostic has to hold for the case to pass.
 */
export const PlaybookTestAssertionsEditor: FC<
  PlaybookTestAssertionsEditorProps
> = (props) => {
  const { playbook, tools, value, onChange } = props;

  const outcomeOptions: readonly SimpleSelectOption<string>[] = useMemo(() => {
    const outcomeDefinition = getOutcomeDefinitionFromPlaybook(playbook);

    return (outcomeDefinition?.results ?? []).map((result) => ({
      value: result.value,
      label: result.label,
    }));
  }, [playbook]);

  return (
    <div className="space-y-4">
      <FieldDescription>
        Every assertion is judged against the call. The case passes when each
        one that is not diagnostic holds.
      </FieldDescription>
      {value.map((assertion, index) => (
        <PlaybookTestAssertionRow
          key={assertion.id}
          assertion={assertion}
          outcomeOptions={outcomeOptions}
          tools={tools}
          onChange={(newAssertion) =>
            onChange(
              value.map((existing, existingIndex) =>
                existingIndex === index ? newAssertion : existing,
              ),
            )
          }
          onRemove={() =>
            onChange(
              value.filter((_, existingIndex) => existingIndex !== index),
            )
          }
        />
      ))}
      <MenuTrigger>
        <Button
          variant={ButtonVariant.Secondary}
          size={Size.MEDIUM}
          icon={<IconPlus className="h-4 w-4" />}
        >
          Add assertion
        </Button>
        <SimpleMenuPopover>
          <SimpleMenu aria-label="Assertion kinds">
            {allCallTestAssertionKinds.map((kind) => (
              <SimpleMenuItem
                key={kind}
                textValue={assertionKindLabels[kind]}
                onAction={() =>
                  onChange([...value, createPlaybookTestAssertion(kind)])
                }
              >
                {assertionKindLabels[kind]}
              </SimpleMenuItem>
            ))}
          </SimpleMenu>
        </SimpleMenuPopover>
      </MenuTrigger>
    </div>
  );
};

export interface PlaybookTestAssertionsSectionProps {
  playbook: AdminApiPlaybook | undefined;
  value: readonly PlaybookTestAssertion[] | null;
  onChange: (value: PlaybookTestAssertion[]) => void;
}

/**
 * The editor behind a collapsed disclosure that says how many claims the case
 * makes, so the count is readable without opening it.
 */
export const PlaybookTestAssertionsSection: FC<
  PlaybookTestAssertionsSectionProps
> = (props) => {
  const { playbook, value, onChange } = props;

  const tools = useAttachedAgentTools();

  return (
    <Disclosure className="rounded-lg border border-border-light bg-white">
      <Heading>
        <AriaButton
          slot="trigger"
          className="group w-full min-w-0 cursor-pointer text-left"
        >
          <div className="flex items-center gap-3 px-3 py-2.5">
            <IconChecklist className="size-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1 text-sm font-medium text-foreground">
              Expectations
            </div>
            <div className="min-w-0 truncate text-sm text-muted-foreground">
              {describePlaybookTestAssertions(value)}
            </div>
            <IconChevronDown className="size-4 shrink-0 text-gray-400 transition-transform group-aria-expanded:rotate-180" />
          </div>
        </AriaButton>
      </Heading>
      <DisclosurePanel>
        <div className="border-t border-border-light px-3 py-3">
          <PlaybookTestAssertionsEditor
            playbook={playbook}
            tools={tools}
            value={value ?? []}
            onChange={onChange}
          />
        </div>
      </DisclosurePanel>
    </Disclosure>
  );
};
